import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/layout/AppLayout';
import useWindowWidth from '../hooks/useWindowWidth';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3002';
const POLL_INTERVAL = 2000;

const AGENT_COLORS = ['#0052CC', '#00875A', '#FF5630', '#6554C0', '#FF8B00', '#00B8D9'];

const styles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  title: { fontSize: '20px', fontWeight: '700', color: '#111827' },
  scenarioSub: { fontSize: '13px', color: '#6b7280', marginTop: '2px' },
  layout: { display: 'grid', gridTemplateColumns: '1fr 320px', gap: '24px', alignItems: 'start' },
  transcriptCard: {
    backgroundColor: '#fff', borderRadius: '12px', padding: '24px',
    border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    minHeight: '400px',
  },
  controlCard: {
    backgroundColor: '#fff', borderRadius: '12px', padding: '24px',
    border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  turnBlock: {
    marginBottom: '16px', padding: '14px 16px', borderRadius: '10px',
    border: '1px solid #f3f4f6', backgroundColor: '#fafafa',
  },
  turnHeader: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' },
  turnLabel: { fontSize: '11px', fontWeight: '700', color: '#9ca3af' },
  agentName: { fontSize: '13px', fontWeight: '700' },
  turnContent: { fontSize: '14px', color: '#374151', lineHeight: '1.6' },
  perceived: {
    fontSize: '12px', color: '#6b7280', marginTop: '8px', padding: '6px 10px',
    backgroundColor: '#fef3c7', borderRadius: '6px', borderLeft: '3px solid #d97706',
  },
  statusBadge: {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600',
  },
  progressTrack: {
    height: '6px', backgroundColor: '#e5e7eb', borderRadius: '3px',
    overflow: 'hidden', marginBottom: '16px',
  },
  progressFill: { height: '100%', borderRadius: '3px', backgroundColor: '#0052CC', transition: 'width 0.5s ease' },
  stopBtn: {
    width: '100%', padding: '10px', borderRadius: '8px', border: 'none',
    cursor: 'pointer', fontSize: '13px', fontWeight: '600', fontFamily: 'inherit',
    marginBottom: '8px',
  },
  runBtn: {
    width: '100%', padding: '10px', borderRadius: '8px', border: 'none',
    cursor: 'pointer', fontSize: '13px', fontWeight: '600', fontFamily: 'inherit',
    backgroundColor: '#0052CC', color: '#fff', marginBottom: '8px',
  },
  backBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '13px', color: '#6b7280', fontFamily: 'inherit', padding: 0,
    marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '4px',
  },
  agentLegend: { marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f3f4f6' },
  agentLegendItem: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', fontSize: '12px', color: '#374151' },
  agentDot: { width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0 },
  empty: { fontSize: '14px', color: '#9ca3af', textAlign: 'center', padding: '40px 0' },
  error: {
    backgroundColor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px',
    padding: '12px 16px', fontSize: '13px', color: '#dc2626', marginBottom: '16px',
  },
};

const STATUS_STYLES = {
  running: { backgroundColor: '#dbeafe', color: '#0052CC' },
  completed: { backgroundColor: '#dcfce7', color: '#16a34a' },
  failed: { backgroundColor: '#fef2f2', color: '#dc2626' },
  stopped: { backgroundColor: '#f3f4f6', color: '#6b7280' },
};

export default function SimulationRunner() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const scenarioId = searchParams.get('scenario');
  const width = useWindowWidth();
  const isMobile = width < 768;

  const [simulation, setSimulation] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [turns, setTurns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [limitModalMessage, setLimitModalMessage] = useState('');
  const [visibleUpToTurn, setVisibleUpToTurn] = useState(1);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);

  const pollRef = useRef(null);
  const bottomRef = useRef(null);
  const simulationRef = useRef(null);
  const isRunningRef = useRef(false);
  const visibleUpToTurnRef = useRef(1);
  const playedTurnsRef = useRef(new Set());
  const audioElementRef = useRef(null);
  const audioPlayingRef = useRef(false);
  const audioUnlockedRef = useRef(false);
  
  useEffect(() => {
    if (simulation?.status === 'limit_reached' && simulation?.limit_message) {
      setLimitModalMessage(simulation.limit_message);
    }
  }, [simulation]);

  useEffect(() => {
    // Create persistent audio element
    const audioEl = new Audio();
    audioEl.preload = 'auto';
    audioElementRef.current = audioEl;

    if (scenarioId) fetchScenarioAndStart();

    return () => {
      clearInterval(pollRef.current);
      isRunningRef.current = false;
      if (audioElementRef.current) {
        audioElementRef.current.pause();
        audioElementRef.current.src = '';
      }
      audioPlayingRef.current = false;
    };
  }, [scenarioId]);

  // Auto-scroll — desktop only
  useEffect(() => {
    if (!bottomRef.current || isMobile) return;
    bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [turns, isMobile]);

  function unlockAudio() {
    if (audioUnlockedRef.current || !audioElementRef.current) return;
    audioElementRef.current.src = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4LjI5LjEwMAAAAAAAAAAAAAAA//OEAAAAAAAAAAAAAAAAAAAAAAAASW5mbwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjU0AAAAAAAAAAAAAAAAJAAAAAAAAAAAAYbhBZOpAAAAAAAAAAAAAAAAAAAA//OEZAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAACcQCenp6enp6enp6enp6enp6enp6enp6enp6enp6enp6enp6enp6enp6enp6e//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjU0AAAAAAAAAAAAAAAAJAAAAAAAAAAAAnEhBZOpAAAAAAAAAAAAAAAAAAAA';
    audioElementRef.current.play().then(() => {
      audioUnlockedRef.current = true;
      audioElementRef.current.pause();
      audioElementRef.current.src = '';
      console.log('[Audio] Element unlocked on mobile');
    }).catch(() => {});
  }

  async function fetchScenarioAndStart() {
    if (!scenarioId || !currentUser) return;
    setLoading(true);
    setError('');
    try {
      const scenRes = await fetch(`${API}/api/scenarios/${scenarioId}`);
      if (!scenRes.ok) throw new Error('Scenario not found');
      const scenData = await scenRes.json();
      setScenario(scenData);
      await startSimulation(scenData);
    } catch (err) {
      setError(err.message || 'Failed to start simulation');
      setLoading(false);
    }
  }

  async function startSimulation(scenData) {
    setLoading(true);
    setError('');
    setTurns([]);
    setVisibleUpToTurn(1);
    visibleUpToTurnRef.current = 1;
    playedTurnsRef.current = new Set();
    audioPlayingRef.current = false;
    setIsPlayingAudio(false);

    try {
      const res = await fetch(`${API}/api/simulations/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scenario_id: scenarioId, owner_id: currentUser.uid }),
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to start simulation');
      }
      const simData = await res.json();
      setSimulation(simData);
      simulationRef.current = simData;
      isRunningRef.current = true;
      setLoading(false);
      startPolling(simData.id, scenData || scenario);
    } catch (err) {
      setError(err.message || 'Failed to start simulation');
      setLoading(false);
    }
  }

  function startPolling(simulationId, scenData) {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      if (!isRunningRef.current) { clearInterval(pollRef.current); return; }
      try {
        const [simRes, turnsRes] = await Promise.all([
          fetch(`${API}/api/simulations/${simulationId}`),
          fetch(`${API}/api/simulations/${simulationId}/transcript`),
        ]);
        const simData = await simRes.json();
        const turnsData = await turnsRes.json();
        simulationRef.current = simData;
        setSimulation(simData);
        setTurns(Array.isArray(turnsData) ? turnsData : []);

        const currentVisible = visibleUpToTurnRef.current;
        const currentTurn = turnsData.find(t => t.turn_number === currentVisible);
        console.log(`[Poll] turn ${currentVisible} data:`, currentTurn ? { audio_url: !!currentTurn.audio_url, content: currentTurn.content?.substring(0, 30) } : 'NOT FOUND');
        const audioSrc = currentTurn?.audio_url || null;
        const hasAudio = !!audioSrc;
        const alreadyPlayed = playedTurnsRef.current.has(currentVisible);
        console.log(`[Poll] visibleTurn=${currentVisible} audioPlaying=${audioPlayingRef.current} played=${alreadyPlayed} hasAudio=${hasAudio}`);

        const outputMode = scenData?.output_mode || simData?.output_mode || 'text';

        if (outputMode === 'text') {
          if (simData.current_turn > currentVisible) {
            setVisibleUpToTurn(simData.current_turn);
            visibleUpToTurnRef.current = simData.current_turn;
          }
        } else {
          if (hasAudio && !alreadyPlayed && !audioPlayingRef.current) {
            console.log(`[Poll] Playing audio for turn ${currentVisible}`);
            playedTurnsRef.current.add(currentVisible);
            playAudio(audioSrc, currentVisible, simulationId, simData.status);
          }
        }

        if (['completed', 'stopped', 'failed', 'limit_reached'].includes(simData.status)) {
          clearInterval(pollRef.current);
          isRunningRef.current = false;
          setVisibleUpToTurn(999);
        }
      } catch { }
    }, POLL_INTERVAL);
  }

  function playAudio(url, turnNumber, simulationId, simStatus) {
    if (audioPlayingRef.current) return;
    audioPlayingRef.current = true;
    setIsPlayingAudio(true);
    console.log(`[Audio] Starting playback for turn ${turnNumber}`);

    const audio = audioElementRef.current || new Audio();
    audio.src = url;

    const handleEnded = async () => {
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      console.log(`[Audio] ended event fired for turn ${turnNumber}`);
      audioPlayingRef.current = false;
      setIsPlayingAudio(false);
      const next = turnNumber + 1;
      setVisibleUpToTurn(next);
      visibleUpToTurnRef.current = next;
      console.log(`[Audio] calling next-turn for simulation ${simulationId}, status=${simStatus}`);
      if (simStatus === 'running') {
        try {
          const res = await fetch(`${API}/api/simulations/${simulationId}/next-turn`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
          });
          const data = await res.json();
          console.log(`[Audio] next-turn response:`, JSON.stringify(data));
          if (data.status === 'completed' || data.status === 'stopped' || data.status === 'failed') {
            clearInterval(pollRef.current);
            isRunningRef.current = false;
            setSimulation(prev => ({ ...prev, status: data.status }));
            setVisibleUpToTurn(999);
          } else if (data.audio_url && data.turn) {
            const nextTurnNum = data.turn;
            playedTurnsRef.current.add(nextTurnNum);
            playAudio(data.audio_url, nextTurnNum, simulationId, 'running');
          }
        } catch (err) {
          console.error('[Audio] next-turn error:', err);
        }
      }
    };

    const handleError = (e) => {
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      console.error(`[Audio] error for turn ${turnNumber}:`, e);
      audioPlayingRef.current = false;
      setIsPlayingAudio(false);
      const next = turnNumber + 1;
      setVisibleUpToTurn(next);
      visibleUpToTurnRef.current = next;
    };

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    audio.play().then(() => {
      console.log(`[Audio] play() resolved for turn ${turnNumber}`);
    }).catch((err) => {
      console.error(`[Audio] play() rejected for turn ${turnNumber}:`, err);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audioPlayingRef.current = false;
      setIsPlayingAudio(false);
    });
  }

  async function stopSimulation() {
    if (!simulation) return;
    try {
      await fetch(`${API}/api/simulations/${simulation.id}/stop`, { method: 'POST' });
      clearInterval(pollRef.current);
      isRunningRef.current = false;
      setSimulation(prev => ({ ...prev, status: 'stopped' }));
    } catch {
      setError('Failed to stop simulation.');
    }
  }

  const isRunning = simulation?.status === 'running';
  const isComplete = simulation?.status === 'completed';
  const isStopped = simulation?.status === 'stopped';
  const progress = simulation?.max_turns
    ? Math.min(100, ((simulation.current_turn || 0) / simulation.max_turns) * 100)
    : 0;

  const agentIds = simulation?.turn_order
    ? [...new Set(simulation.turn_order)]
    : [];
  const agentColorMap = {};
  agentIds.forEach((id, i) => { agentColorMap[id] = AGENT_COLORS[i % AGENT_COLORS.length]; });

  const visibleTurns = turns.filter(t => t.turn_number <= visibleUpToTurn);

  return (
    <AppLayout>
      <button
        style={styles.backBtn}
        onClick={() => {
          clearInterval(pollRef.current);
          if (audioElementRef.current) { audioElementRef.current.pause(); audioElementRef.current.src = ''; }
          audioPlayingRef.current = false;
          isRunningRef.current = false;
          navigate('/scenarios');
        }}
      >
        ← Scenarios
      </button>

      <div style={{ ...styles.header, flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? '8px' : '0' }}>
        <div>
          <div style={styles.title}>{scenario?.title || 'Simulation'}</div>
          {simulation && (
            <div style={styles.scenarioSub}>
              Turn {simulation.current_turn || 0} of {simulation.max_turns} ·{' '}
              <span style={{ ...styles.statusBadge, ...STATUS_STYLES[simulation.status] }}>
                {simulation.status}
              </span>
            </div>
          )}
        </div>
      </div>

      {limitModalMessage && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 300,
        }}>
          <div style={{
            backgroundColor: '#fff', borderRadius: '16px', padding: '32px',
            maxWidth: '420px', width: '90%', textAlign: 'center',
            boxShadow: '0 24px 64px rgba(0,0,0,0.2)',
          }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>⏳</div>
            <div style={{ fontSize: '18px', fontWeight: '700', color: '#111827', marginBottom: '8px' }}>
              Daily Turn Limit Reached
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px', lineHeight: '1.6' }}>
              {limitModalMessage}
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#f3f4f6', color: '#374151', fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }}
                onClick={() => setLimitModalMessage('')}
              >
                Dismiss
              </button>
              <button
                style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', backgroundColor: '#0052CC', color: '#fff', fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }}
                onClick={() => navigate('/pricing')}
              >
                Upgrade to Professional →
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ ...styles.layout, gridTemplateColumns: isMobile ? '1fr' : '1fr 320px' }}>
        {/* Transcript */}
        <div style={styles.transcriptCard}>
          <div style={{ fontSize: '13px', fontWeight: '700', color: '#0052CC', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '16px' }}>
            Conversation Transcript
          </div>

          {loading && (
            <div style={styles.empty}>Starting simulation...</div>
          )}

          {!loading && visibleTurns.length === 0 && (
            <div style={styles.empty}>Waiting for first turn...</div>
          )}

          {visibleTurns.map(turn => {
            const color = agentColorMap[turn.agent_id] || '#0052CC';
            return (
              <div key={`${turn.turn_number}-${turn.agent_id}`} style={styles.turnBlock}>
                <div style={styles.turnHeader}>
                  <div style={{ ...styles.agentDot, backgroundColor: color }} />
                  <span style={{ ...styles.agentName, color }}>{turn.agent_name}</span>
                  {turn.agent_role && (
                    <span style={{ fontSize: '11px', color: '#9ca3af' }}>· {turn.agent_role}</span>
                  )}
                  <span style={{ ...styles.turnLabel, marginLeft: 'auto' }}>Turn {turn.turn_number}</span>
                </div>
                <div style={styles.turnContent}>{turn.content}</div>
                {turn.perceived_content && turn.perturbation_applied && (
                  <div style={styles.perceived}>
                    🎙 Perceived: "{turn.perceived_content}"
                  </div>
                )}
                {turn.perturbation_applied && (
                  <div style={{ marginTop: '6px' }}>
                    <span style={{
                      fontSize: '10px', fontWeight: '700', padding: '2px 7px', borderRadius: '10px',
                      backgroundColor: turn.perturbation_mode === 'parametric' ? '#dbeafe' : '#f3f4f6',
                      color: turn.perturbation_mode === 'parametric' ? '#0052CC' : '#6b7280',
                    }}>
                      {turn.perturbation_mode === 'parametric' ? '✦ Parametric' : 'Categorical'}
                    </span>
                  </div>
                )}
                {turn.audio_url && (
                  <audio controls src={turn.audio_url} style={{ width: '100%', marginTop: '8px', height: '32px' }} />
                )}
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        {/* Controls */}
        <div style={styles.controlCard}>
          {/* Progress */}
          {simulation && (
            <>
              <div style={styles.progressTrack}>
                <div style={{ ...styles.progressFill, width: `${progress}%` }} />
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px', textAlign: 'center' }}>
                {simulation.current_turn || 0} / {simulation.max_turns} turns
                {isPlayingAudio && ' · 🔊 Playing...'}
              </div>
            </>
          )}

          {/* Buttons */}
          {isRunning && (
            <button style={{ ...styles.stopBtn, backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5' }} onClick={stopSimulation}>
              ⏹ Stop Simulation
            </button>
          )}

          {isComplete && (
            <button style={styles.runBtn} onClick={() => navigate(`/results/${simulation.id}`)}>
              View Results →
            </button>
          )}

          {(isComplete || isStopped) && (
            <button
              style={{ ...styles.stopBtn, backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb' }}
              onClick={() => { unlockAudio(); startSimulation(scenario); }}
            >
              ↺ Run Again
            </button>
          )}

          {/* Agent legend */}
          {agentIds.length > 0 && (
            <div style={styles.agentLegend}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                Agents
              </div>
              {agentIds.map(id => {
                const turn = turns.find(t => t.agent_id === id);
                return turn ? (
                  <div key={id} style={styles.agentLegendItem}>
                    <div style={{ ...styles.agentDot, backgroundColor: agentColorMap[id] }} />
                    <span>{turn.agent_name}{turn.agent_role ? ` · ${turn.agent_role}` : ''}</span>
                  </div>
                ) : null;
              })}
            </div>
          )}

          {/* Output mode */}
          {scenario && (
            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f3f4f6', fontSize: '12px', color: '#9ca3af' }}>
              Mode: {scenario.output_mode === 'voice_stt' ? '🔊🎙 Voice + STT' : scenario.output_mode === 'voice' ? '🔊 Voice' : '📝 Text'}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
